import {
    type AnonymousProfile,
    generateAnonymousProfile,
    getAnonymousModeEnabled,
    getAnonymousProfile,
    saveAnonymousProfile as persistAnonymousProfile,
    setAnonymousModeEnabled as saveAnonymousModeEnabled
} from "@/utils/anonymousProfile";
import { useCallback, useState } from "react";

interface UseAnonymousProfileReturn {
    isEnabled: boolean;
    profile: AnonymousProfile | null;
    toggleEnabled: () => void;
    regenerateProfile: () => void;
    setProfile: (profile: AnonymousProfile, enabled: boolean) => void;
}

/**
 * 익명 프로필 관리 훅
 * localStorage와 동기화하여 익명 모드 상태 및 프로필 관리
 */
export const useAnonymousProfile = (): UseAnonymousProfileReturn => {
    // localStorage에서 초기 상태를 동기적으로 읽어 초기값 설정
    const [isEnabled, setIsEnabled] = useState<boolean>(() => {
        const enabled = getAnonymousModeEnabled();
        // 설정된 적 없으면 기본값 true (익명 모드 ON)
        if (enabled === null) {
            const defaultEnabled = true;
            saveAnonymousModeEnabled(defaultEnabled);
            return defaultEnabled;
        }
        return enabled;
    });

    const [profile, setProfileState] = useState<AnonymousProfile | null>(() => {
        // 프로필은 저장된 것만 로드 (자동 생성 안 함)
        return getAnonymousProfile();
    });

    const toggleEnabled = useCallback(() => {
        const newEnabled = !isEnabled;
        setIsEnabled(newEnabled);
        saveAnonymousModeEnabled(newEnabled);
    }, [isEnabled]);

    const regenerateProfile = useCallback(() => {
        const newProfile = generateAnonymousProfile();
        persistAnonymousProfile(newProfile);
        setProfileState(newProfile);
    }, []);

    const setProfile = useCallback(
        (newProfile: AnonymousProfile, enabled: boolean) => {
            persistAnonymousProfile(newProfile);
            saveAnonymousModeEnabled(enabled);
            setProfileState(newProfile);
            setIsEnabled(enabled);
        },
        []
    );

    return {
        isEnabled,
        profile,
        toggleEnabled,
        regenerateProfile,
        setProfile
    };
};
